/**
 * AuthGate — the boot guard. On mount it attempts a silent refresh; on success
 * it renders the protected app shell. On failure it stashes the intended path
 * and redirects the browser to centralized login.
 *
 * Mounting the gate at the root of authenticated routes means a deep-link load
 * lands the user back where they wanted to go after sign-in.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PreAppLayout } from '@/components/PreAppLayout';
import { bootRefresh, redirectToCentralizedLogin } from './silentRefresh';
import { getAccessToken } from './authStore';
import { useAuth } from './AuthContext';
import { useCopy } from '@/hooks/useCopy';

type GateState = 'booting' | 'authed' | 'redirecting';

export function AuthGate(): ReactNode {
  const location = useLocation();
  const { setAuthed } = useAuth();
  const [state, setState] = useState<GateState>(() => (getAccessToken() ? 'authed' : 'booting'));
  const started = useRef(false);
  const { t } = useCopy();

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      if (getAccessToken()) {
        setState('authed');
        return;
      }
      const ok = await bootRefresh();
      if (ok) {
        const token = getAccessToken();
        if (token) setAuthed(token, null);
        setState('authed');
      } else {
        setState('redirecting');
        redirectToCentralizedLogin(location.pathname + location.search);
      }
    })();
  }, [location.pathname, location.search, setAuthed]);

  if (state === 'authed') {
    return <Outlet />;
  }
  return (
    <PreAppLayout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <LoadingSpinner />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {state === 'redirecting'
            ? t('auth.redirecting', 'Redirecting to sign in…')
            : t('app.loading', 'Loading')}
        </Typography>
      </Box>
    </PreAppLayout>
  );
}
